import { I18nManager } from 'react-native';
import type { User } from '@/core/stores/appStore';

/**
 * User utility functions
 * Centralized user data processing logic to avoid duplication
 */

/**
 * Extracts the first name (or first portion) from user data
 * Priority order:
 * 1. firstnameAR (if in RTL mode)
 * 2. firstnameEN (if available, use directly)
 * 3. full_name (extract first part by splitting on space)
 * 4. name (extract first part by splitting on space)
 * 5. fallback (default: 'User')
 * 
 * @param user - User object from appStore
 * @param fallback - Fallback value if no name is found (default: 'User')
 * @returns First name or fallback value
 */
export function getUserFirstName(user: User | null | undefined, fallback: string = 'User'): string {
    if (!user) {
        return fallback;
    }

    if (I18nManager.isRTL && user.firstnameAR) {
        return user.firstnameAR;
    }

    // Priority 1: Use firstnameEN if available (already just first name)
    if (user.firstnameEN) {
        return user.firstnameEN;
    }

    // Priority 2: Extract first part from full_name
    if (user.full_name) {
        const firstPart = user.full_name.split(' ')[0]?.trim();
        if (firstPart) {
            return firstPart;
        }
    }

    // Priority 3: Extract first part from name
    if (user.name) {
        const firstPart = user.name.split(' ')[0]?.trim();
        if (firstPart) {
            return firstPart;
        }
    }

    // Fallback
    return fallback;
}

/**
 * Extracts the full name from user data
 * Priority order:
 * 1. firstnameAR + lastnameAR (if in RTL mode)
 * 2. full_name
 * 3. firstnameEN + lastnameEN
 * 4. name
 * 5. fallback
 */
export function getUserFullName(user: User | null | undefined, isRTL: boolean = false, fallback: string = ''): string {
    if (!user) return fallback;

    if (isRTL) {
        const firstAR = typeof user.firstnameAR === "string" ? user.firstnameAR.trim() : "";
        const lastAR = typeof user.lastnameAR === "string" ? user.lastnameAR.trim() : "";
        const fullAR = `${firstAR} ${lastAR}`.trim();
        if (fullAR) return fullAR;
    }

    if (typeof user.full_name === "string" && user.full_name.trim()) {
        return user.full_name.trim();
    }

    const firstEN = typeof user.firstnameEN === "string" ? user.firstnameEN.trim() : "";
    const lastEN = typeof user.lastnameEN === "string" ? user.lastnameEN.trim() : "";
    const fullEN = `${firstEN} ${lastEN}`.trim();
    if (fullEN) return fullEN;

    if (typeof user.name === "string" && user.name.trim()) {
        return user.name.trim();
    }

    return fallback;
}
